import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowRight, ClipboardList, Copy } from "lucide-react";
import { toast } from "sonner";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  CARDS,
  CURRENCIES,
  DATA_KINDS,
  INTEREST,
  KNOWS,
  OFFERS,
  PAY_ANY,
  PAY_MODELS,
  PERSON,
  SNAPSHOT_DATE,
  SOURCE_LABEL,
  type Card,
} from "@/data/survey";
import { supabase } from "@/integrations/supabase/client";
import { submitSurvey } from "@/lib/survey.functions";

// The survey (2026-10-19, owner; design/21_survey.md in the backend repo): 5-7 minutes, one public link people pass
// on, no tokens. v2: one step per page, saved at every step (a survey left half-way still counts), no product names,
// big images. Examples are one person's own data, each marked as whole genome (WGS) or 23andMe array. A visitor
// signed in with reports of their own gets the internal variant (the server decides; this page adapts its wording).

const SURVEY_URL = "https://understandyourgenome.org/survey";
const STORE_KEY = "uyg-survey-v2";

export const Route = createFileRoute("/survey")({
  head: () => ({
    meta: [
      { title: "5-minute survey — Understand Your Genome" },
      {
        name: "description",
        content:
          "Which DNA features would you actually use, and what would they be worth to you? Five minutes, with real example results.",
      },
      { property: "og:title", content: "5-minute survey — Understand Your Genome" },
      {
        property: "og:description",
        content: "Help decide what gets built next: real example results from a whole genome and a 23andMe array.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SurveyPage,
});

type Interest = "no" | "nice" | "want";
type Price = { amount: number | null; none: boolean };
type State = {
  client_id: string;
  step: number;
  name: string;
  referrer: string;
  knows: (typeof KNOWS)[number]["id"] | null;
  dataKinds: string[];
  ownReport: string;
  cards: Record<string, Interest>;
  top: string | null;
  payAny: "yes" | "maybe" | "no" | null;
  currency: (typeof CURRENCIES)[number];
  prices: Record<string, Price>;
  payModel: (typeof PAY_MODELS)[number]["id"] | null;
  missing: string;
  email: string;
  wantsInterview: boolean;
  wantsEarly: boolean;
  sharesData: boolean;
};

const STEPS = ["welcome", "about", ...CARDS.map((c) => c.id), "top", "pay", "end"];

function freshState(): State {
  return {
    client_id: crypto.randomUUID(),
    step: 0,
    name: "",
    referrer: "",
    knows: null,
    dataKinds: [],
    ownReport: "",
    cards: {},
    top: null,
    payAny: null,
    currency: "EUR",
    prices: {},
    payModel: null,
    missing: "",
    email: "",
    wantsInterview: false,
    wantsEarly: false,
    sharesData: false,
  };
}

function loadState(): State {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (raw) return { ...freshState(), ...JSON.parse(raw) };
  } catch {
    // private mode / blocked storage: start fresh
  }
  return freshState();
}

function Choice<T extends string>({
  options,
  value,
  onChange,
  name,
}: {
  options: readonly { id: T; label: string }[];
  value: T | null;
  onChange: (v: T) => void;
  name: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`rounded-full border px-4 py-2 text-sm transition-colors ${
            value === o.id
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background hover:bg-muted"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SourceBadge({ source }: { source: "wgs" | "array" }) {
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
        source === "wgs" ? "bg-primary/10 text-primary" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
      }`}
    >
      {SOURCE_LABEL[source]}
    </span>
  );
}

function FeatureStep({ card, value, onChange }: { card: Card; value: Interest | null; onChange: (v: Interest) => void }) {
  const hasWgs = card.rows?.some((r) => r.wgs);
  const hasArray = card.rows?.some((r) => r.array);
  return (
    <div>
      <p className="text-xs text-muted-foreground">
        {card.free ? "Free" : "Could be a paid extra"} · <span className="capitalize">{card.status}</span>
      </p>
      <h2 className="mt-1 font-display text-2xl text-foreground">{card.title}</h2>
      <p className="mt-2 leading-relaxed text-muted-foreground">{card.what}</p>

      {card.images?.map((img) => (
        <figure key={img.src} className="mt-5">
          <a href={img.full ?? img.src} target="_blank" rel="noreferrer" title="Open full size">
            <img src={img.src} alt={img.alt} className="w-full rounded-xl border border-border" />
          </a>
          <figcaption className="mt-1 text-xs text-muted-foreground">
            {PERSON} · <SourceBadge source={img.source} />
          </figcaption>
        </figure>
      ))}

      {card.rows && card.rows.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left align-bottom">
                <th className="py-1 pr-3 font-normal text-muted-foreground">{PERSON}</th>
                {hasWgs && (
                  <th className="py-1 pr-3 font-normal">
                    <SourceBadge source="wgs" />
                  </th>
                )}
                {hasArray && (
                  <th className="py-1 font-normal">
                    <SourceBadge source="array" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {card.rows.map((r) => (
                <tr key={r.label} className="border-t border-border align-top">
                  <td className="py-1.5 pr-3">
                    {r.label}
                    {r.note && <div className="text-xs text-muted-foreground">{r.note}</div>}
                  </td>
                  {hasWgs && <td className="py-1.5 pr-3">{r.wgs ?? "–"}</td>}
                  {hasArray && <td className="py-1.5">{r.array ?? "–"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {card.example_note && <p className="mt-3 text-sm text-muted-foreground">{card.example_note}</p>}

      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-foreground">How interesting is this to you?</p>
        <Choice options={INTEREST} value={value} onChange={onChange} name={`Interest in ${card.title}`} />
      </div>
    </div>
  );
}

function SurveyPage() {
  const send = useServerFn(submitSurvey);
  const started = useRef(Date.now());
  const [st, setSt] = useState<State>(freshState);
  const [loaded, setLoaded] = useState(false);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [website, setWebsite] = useState("");
  const top = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSt(loadState());
    setLoaded(true);
    // wording only: signed in with a report of one's own -> the internal variant (the server checks again)
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) return;
      const { count } = await supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("user_id", data.session.user.id);
      if ((count ?? 0) > 0) setInternal(true);
    });
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(st));
    } catch {
      // storage unavailable: the survey still works, a reload starts over
    }
  }, [st, loaded]);

  const set = (patch: Partial<State>) => setSt((prev) => ({ ...prev, ...patch }));
  const setPrice = (id: string, p: Partial<Price>) =>
    setSt((prev) => ({ ...prev, prices: { ...prev.prices, [id]: { amount: null, none: false, ...prev.prices[id], ...p } } }));

  const payload = (final: boolean, page: number) => ({
    client_id: st.client_id,
    page,
    final,
    name: st.name.trim(),
    referrer: st.referrer,
    knows: st.knows,
    data_kinds: internal ? [] : st.dataKinds,
    own_report: internal ? st.ownReport : "",
    cards: st.cards,
    top: st.top,
    pay_any: st.payAny,
    prices: st.prices,
    currency: st.currency,
    pay_model: st.payModel,
    missing: st.missing,
    shares_data: st.sharesData,
    email: st.email.trim(),
    wants_interview: st.wantsInterview,
    wants_early_access: st.wantsEarly,
    duration_s: Math.round((Date.now() - started.current) / 1000),
    website,
  });

  const go = (step: number) => {
    set({ step });
    top.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const next = () => {
    if (STEPS[st.step] === "about" && !st.name.trim()) {
      toast.error("Please tell us your name (a nickname is fine).");
      return;
    }
    // save in the background from the first step after the name: a survey left half-way still counts
    if (st.step >= 1) send({ data: payload(false, st.step + 1) }).catch(() => undefined);
    go(st.step + 1);
  };

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await send({ data: payload(true, st.step) });
      setSent(true);
      try {
        sessionStorage.removeItem(STORE_KEY);
      } catch {
        // nothing to clean up
      }
      top.current?.scrollIntoView({ block: "start" });
    } catch (err) {
      const msg = err instanceof Error && err.message ? err.message : "";
      toast.error(
        msg.includes("connection") || msg.includes("already")
          ? msg
          : "We couldn't save that just now. Please try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  };

  const stepId = STEPS[st.step];
  const card = CARDS.find((c) => c.id === stepId);
  const featureNo = card ? CARDS.indexOf(card) + 1 : 0;

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 py-10">
        <div ref={top} className="scroll-mt-24" />
        {sent ? (
          <div className="rounded-2xl border border-border bg-card p-6">
            <h1 className="font-display text-2xl">Thank you, {st.name.trim()}.</h1>
            <p className="mt-2 leading-relaxed text-muted-foreground">
              Your answers are saved. If you know someone who has done a DNA test (or is curious about one),
              please pass the link on:
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-2 py-1 text-sm">{SURVEY_URL}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(SURVEY_URL).then(() => toast.success("Link copied"))}
              >
                <Copy className="mr-1 size-4" /> Copy link
              </Button>
            </div>
          </div>
        ) : (
          <>
            {st.step > 0 && (
              <div className="mb-6">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>{card ? `Feature ${featureNo} of ${CARDS.length}` : " "}</span>
                  <span>
                    Step {st.step} of {STEPS.length - 1}
                  </span>
                </div>
                <Progress value={(100 * st.step) / (STEPS.length - 1)} />
              </div>
            )}

            {/* honeypot: hidden from people, bots fill it in */}
            <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </label>
            </div>

            {stepId === "welcome" && (
              <div>
                <div className="flex items-center gap-3 text-primary">
                  <ClipboardList className="size-7" />
                  <h1 className="font-display text-3xl text-foreground">Five minutes: what should we build?</h1>
                </div>
                <div className="mt-4 space-y-3 leading-relaxed text-muted-foreground">
                  <p>
                    I'm Charles. I build <strong>understandyourgenome.org</strong>, which is free and stays free, and
                    I'm thinking about a few extras on top of it. I'd like to know which features you would actually
                    use, and what the extras would be worth to you.
                  </p>
                  <p>
                    You'll see {CARDS.length} features, one per page, each with a real example: my own data from two
                    different files, my <SourceBadge source="wgs" /> and my <SourceBadge source="array" />, so you can
                    see where they agree and where an array can't tell. Snapshot of {SNAPSHOT_DATE}.
                  </p>
                </div>
                {internal && (
                  <p className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm">
                    You're signed in with your own report, so your answers are linked to your account, and there is
                    one extra question about your report.
                  </p>
                )}
                <details className="mt-4 rounded-xl border border-border p-3 text-sm text-muted-foreground">
                  <summary className="cursor-pointer text-foreground">What this page stores</summary>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>Your name (a nickname is fine) and your answers, saved at every step.</li>
                    <li>Your e-mail only if you leave it at the end, kept separately so it can be deleted on its own.</li>
                    {internal && <li>A link to your account, because you are signed in.</li>}
                    <li>
                      A scrambled fingerprint of your internet connection, only to stop spam. Your IP address itself
                      is not stored.
                    </li>
                    <li>Nothing about your DNA. To have your answers deleted, write via the Feedback page.</li>
                  </ul>
                </details>
              </div>
            )}

            {stepId === "about" && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl text-foreground">About you</h2>
                <div className="space-y-2">
                  <Label htmlFor="survey-name">Your name or a nickname</Label>
                  <Input id="survey-name" value={st.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} />
                </div>
                <div className="space-y-2">
                  <Label>How do you know about this?</Label>
                  <Choice options={KNOWS} value={st.knows} onChange={(knows) => set({ knows })} name="How do you know about this" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="survey-referrer">
                    Who sent you the link? <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input id="survey-referrer" value={st.referrer} onChange={(e) => set({ referrer: e.target.value })} maxLength={120} />
                </div>
                {internal ? (
                  <div className="space-y-2">
                    <Label htmlFor="survey-own">
                      In your own report: what was most useful, and what confused or worried you?{" "}
                      <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea id="survey-own" value={st.ownReport} onChange={(e) => set({ ownReport: e.target.value })} rows={3} maxLength={2000} />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Which DNA data do you have?</Label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {DATA_KINDS.map((d) => (
                        <label key={d.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={st.dataKinds.includes(d.id)}
                            onCheckedChange={(c) =>
                              set({ dataKinds: c ? [...st.dataKinds, d.id] : st.dataKinds.filter((x) => x !== d.id) })
                            }
                          />
                          {d.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {card && (
              <FeatureStep
                card={card}
                value={st.cards[card.id] ?? null}
                onChange={(v) => set({ cards: { ...st.cards, [card.id]: v } })}
              />
            )}

            {stepId === "top" && (
              <div>
                <h2 className="font-display text-2xl text-foreground">If only one of these existed, which one?</h2>
                <div className="mt-5">
                  <Choice options={CARDS.map((c) => ({ id: c.id, label: c.title }))} value={st.top} onChange={(t) => set({ top: t })} name="Top pick" />
                </div>
              </div>
            )}

            {stepId === "pay" && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl text-foreground">Paying</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  understandyourgenome.org stays free. Some of the extras may cost money one day. No prices are set
                  yet, so there is no right answer: we'd like your honest number.
                </p>
                <div className="space-y-2">
                  <Label>Would you pay for any of these at all?</Label>
                  <Choice options={PAY_ANY} value={st.payAny} onChange={(payAny) => set({ payAny })} name="Would you pay at all" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="survey-currency">Currency</Label>
                  <select
                    id="survey-currency"
                    value={st.currency}
                    onChange={(e) => set({ currency: e.target.value as State["currency"] })}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <Label>The most you would pay</Label>
                  {OFFERS.map((o) => {
                    const p = st.prices[o.id] ?? { amount: null, none: false };
                    return (
                      <div key={o.id} className="rounded-xl border border-border p-3">
                        <p className="text-sm">
                          {o.title} <span className="text-muted-foreground">({o.unit})</span>
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            aria-label={`Most you would pay: ${o.title}`}
                            className="w-32"
                            disabled={p.none}
                            value={p.amount ?? ""}
                            onChange={(e) => setPrice(o.id, { amount: e.target.value === "" ? null : Number(e.target.value) })}
                          />
                          <span className="text-sm text-muted-foreground">{st.currency}</span>
                          <label className="flex items-center gap-2 text-sm">
                            <Checkbox checked={p.none} onCheckedChange={(c) => setPrice(o.id, { none: !!c, amount: null })} />
                            I wouldn't pay for this
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2">
                  <Label>How would you prefer to pay?</Label>
                  <Choice options={PAY_MODELS} value={st.payModel} onChange={(payModel) => set({ payModel })} name="Preferred way to pay" />
                </div>
              </div>
            )}

            {stepId === "end" && (
              <div className="space-y-5">
                <h2 className="font-display text-2xl text-foreground">Anything else?</h2>
                <div className="space-y-2">
                  <Label htmlFor="survey-missing">
                    What's missing, or what would you change? <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Textarea id="survey-missing" value={st.missing} onChange={(e) => set({ missing: e.target.value })} rows={3} maxLength={2000} />
                </div>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox className="mt-0.5" checked={st.sharesData} onCheckedChange={(c) => set({ sharesData: !!c })} />
                  I have both a DNA test (like 23andMe) and a whole genome, and I'd share them to help check how well
                  arrays can stand in for whole genomes.
                </label>
                <div className="space-y-2">
                  <Label htmlFor="survey-email">
                    E-mail <span className="text-muted-foreground">(only if you tick a box here)</span>
                  </Label>
                  <Input id="survey-email" type="email" value={st.email} onChange={(e) => set({ email: e.target.value })} maxLength={255} />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={st.wantsInterview} onCheckedChange={(c) => set({ wantsInterview: !!c })} />
                    I'd do a 20-minute call
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={st.wantsEarly} onCheckedChange={(c) => set({ wantsEarly: !!c })} />
                    Tell me when something I picked is available
                  </label>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3">
              {st.step > 0 ? (
                <Button variant="outline" onClick={() => go(st.step - 1)}>
                  <ArrowLeft className="mr-1 size-4" /> Back
                </Button>
              ) : (
                <span />
              )}
              {stepId === "end" ? (
                <Button size="lg" onClick={finish} disabled={busy}>
                  {busy ? "Sending…" : "Send my answers"}
                </Button>
              ) : (
                <Button size="lg" onClick={next}>
                  {st.step === 0 ? "Start" : "Next"} <ArrowRight className="ml-1 size-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
